import React, { useEffect, useState } from "react";
import BaseLayout from "../../components/Layout";
import { useAppSelector } from "../../hooks/useAppSelector";
import { useAppDispatch } from "../../hooks/useAppDispatch";
import { getUser } from "../../helpers/user";
import {
  Plus,
  X,
  Store,
  Target,
  DollarSign,
  Users,
  Loader2,
  Save,
  Activity,
  MapPin,
  FileText,
  Search,
} from "lucide-react";
import { toast } from "sonner";

import { actionPostAlertasInserirUsuarioEncaminhado } from "../../features/alertas/inserir-encaminhado/slices";
import { actionDeleteAlertasUsuariosEncaminhados } from "../../features/alertas/delete-encaminhado/slices";
import { actionGetAlertasUsuariosEncaminhados } from "../../features/alertas/get-encaminhado/slices";
import {
  actionGetAllNomeDeUsuariosPorLoja,
  actionResetGetAllNomeDeUsuariosPorLoja,
} from "../../features/alertas/getAllNomeDeUsuariosPorLoja/slices";

import "./styles.scss";
import { EncaminhadoItem } from "../Alertas/types";
import {
  actionGetLojaConfig,
  actionResetConfigLoja,
  actionUpdateLojaConfig,
} from "../../features/loja/ConfigLoja/slices";

const ConfigLoja = () => {
  const user = getUser();
  const dispatch = useAppDispatch();

  const [storeConfig, setStoreConfig] = useState({
    nome: "",
    numero: "",
    endereco: "",
    uf: "",
    valorLoja: "",
    ativo: false,
    arquivoPlanta: "",
    latitude: "",
    longitude: "",
    idCoordenacao: 0,
    idRegional: 0,
    idSeguradora: 0,
    idDivisao: 0,
    cnpj: "",
    vigencia: Date.now(),
  });

  const [loadingGeo, setLoadingGeo] = useState(false);

  // Seletores do Redux
  const { data: listaEncaminhados } = useAppSelector(
    (state) => state.getAllUsuariosEncaminhados,
  );

  const listaNomesUsuariosPorLoja = useAppSelector(
    (state) => state.getAllNomeDeUsuariosPorLoja.data || [], // Garante que é ao menos um array vazio
  ) as { id: number; nomeFormatado: string; idLojaOrigem: number }[];

  const { lojaData, statusUpdate } = useAppSelector(
    (state) => state.configLoja,
  );
  const lojaSelectedRedux = useAppSelector(
    (state) => state.getLojas.lojaSelected,
  );

  // Define a Loja Alvo (Selecionada ou a do Usuário)
  const idLojaTarget =
    lojaSelectedRedux && lojaSelectedRedux !== ""
      ? Number(lojaSelectedRedux)
      : null;

  const idRegionalTarget =
    lojaData?.idRegional || user?.usuario?.loja?.idRegional;

  console.log("idLojaTarget, configLoja: ", idLojaTarget);
  console.log("idRegionalTarget, configLoja: ", idRegionalTarget);

  // -----------------------------------------------------------------------
  // EFFECT 1: CARREGA A LISTA GLOBAL DE USUÁRIOS (DIREITA)
  // Dependência apenas do idCliente. Não muda ao trocar de loja.
  // -----------------------------------------------------------------------
  useEffect(() => {
    if (user?.usuario?.idCliente) {
      // idLojaTarget pode ser null (quando selecionado "selecione"),
      // o que agora é aceito pelo seu backend para trazer todos.
      dispatch(
        actionGetAllNomeDeUsuariosPorLoja({
          idCliente: user.usuario.idCliente,
          idLoja: idLojaTarget,
        }),
      );
    }
    return () => {
      dispatch(actionResetGetAllNomeDeUsuariosPorLoja());
    };
  }, [dispatch, user.usuario.idCliente, idLojaTarget]);

  // -----------------------------------------------------------------------
  // EFFECT 2: CARREGA DADOS DA LOJA SELECIONADA (ESQUERDA + VÍNCULOS)
  // Dependência do idLojaTarget. Recarrega ao trocar de loja.
  // -----------------------------------------------------------------------
  useEffect(() => {
    if (idLojaTarget && user?.usuario?.idCliente) {
      const filters = {
        idLoja: idLojaTarget,
        idRegional: idRegionalTarget || 0,
        idCliente: user.usuario.idCliente,
      };

      // 1. Busca quais usuários desta loja específica devem receber alerta (para pintar os botões)
      dispatch(actionGetAlertasUsuariosEncaminhados(filters));

      // 2. Busca os dados do formulário da loja
      dispatch(actionGetLojaConfig(idLojaTarget));
    }

    return () => {
      dispatch(actionResetConfigLoja());
    };
  }, [dispatch, idLojaTarget, idRegionalTarget, user.usuario.idCliente]);

  // Popula o formulário quando os dados da loja chegam
  useEffect(() => {
    if (lojaData) {
      const valorInicial = lojaData.importanciaSegurada
        ? lojaData.importanciaSegurada.toFixed(2)
        : "";

      setStoreConfig({
        nome: lojaData.nome || "",
        numero: lojaData.numero || "",
        endereco: lojaData.endereco || "",
        uf: lojaData.uf || "",
        valorLoja: formatCurrency(valorInicial),
        ativo: Boolean(lojaData.ativo),
        arquivoPlanta: lojaData.arquivoPlanta || "",
        latitude: lojaData.latitude ? String(lojaData.latitude) : "",
        longitude: lojaData.longitude ? String(lojaData.longitude) : "",
        idCoordenacao: lojaData.idCoordenacao,
        idRegional: lojaData.idRegional,
        idSeguradora: lojaData.idSeguradora,
        idDivisao: lojaData.idDivisao ?? 0,
        cnpj: storeConfig.cnpj || "",
        vigencia: Date.now(),
      });
    }
  }, [lojaData]);

  // --- Funções Auxiliares e Handlers ---

  const formatCurrency = (value: string | number) => {
    if (!value) return "";
    const onlyDigits = String(value).replace(/\D/g, "");
    const numberValue = Number(onlyDigits) / 100;
    return numberValue.toLocaleString("pt-BR", {
      style: "currency",
      currency: "BRL",
    });
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = e.target;
    setStoreConfig((prev) => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setStoreConfig((prev) => ({ ...prev, [name]: checked }));
  };

  const handleChangeValor = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const value = e.target.value;
    setStoreConfig((prev) => ({ ...prev, valorLoja: formatCurrency(value) }));
  };

  const handleGetCoordinates = async () => {
    if (!storeConfig.endereco && !storeConfig.uf) {
      toast.warning("Preencha pelo menos o Endereço e UF.");
      return;
    }
    setLoadingGeo(true);
    try {
      let cleanAddress = storeConfig.endereco
        .replace(/\d{5}-\d{3}/g, "")
        .replace(/ - /g, " ")
        .trim();
      const numero = storeConfig.numero || "";
      const uf = storeConfig.uf || "SP";
      const query = cleanAddress.includes(",")
        ? `${cleanAddress}, Brazil`
        : `${numero} ${cleanAddress}, ${uf}, Brazil`;

      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=1`,
      );
      const data = await response.json();

      if (data && data.length > 0) {
        const bestMatch = data[0];
        setStoreConfig((prev) => ({
          ...prev,
          latitude: bestMatch.lat,
          longitude: bestMatch.lon,
        }));
        toast.success("Coordenadas encontradas!");
      } else {
        toast.error("Endereço não localizado. Tente simplificar.");
      }
    } catch (error) {
      toast.error("Erro de conexão ao buscar coordenadas.");
    } finally {
      setLoadingGeo(false);
    }
  };

  // --- Ações de Adicionar/Remover Vínculo ---

  const handleAddEncaminhado = async (itemString: string) => {
    try {
      // 1. Como agora listaNomesUsuariosPorLoja é um array de OBJETOS (UsuarioPreview),
      // precisamos encontrar o objeto que tem o NomeFormatado igual ao itemString clicado.
      const usuarioAlvo = (listaNomesUsuariosPorLoja as any[]).find(
        (u) => u.nomeFormatado === itemString,
      );

      const idLojaFinal =
        idLojaTarget && idLojaTarget !== 0
          ? idLojaTarget
          : usuarioAlvo.idLojaOrigem;

      await dispatch(
        actionPostAlertasInserirUsuarioEncaminhado({
          idUsuario: usuarioAlvo.id,
          idLoja: idLojaFinal,
          idRegional: idRegionalTarget || 0,
          nomeUsuario: itemString.split(" - ").pop(),
          idCliente: user.usuario.idCliente,
        }),
      ).unwrap();

      toast.success(`Usuário adicionado com sucesso.`);

      // 2. Atualiza a lista de quem já está vinculado (lista da esquerda/verde/vermelho)
      dispatch(
        actionGetAlertasUsuariosEncaminhados({
          idLoja: idLojaTarget,
          idRegional: idRegionalTarget,
          idCliente: user.usuario.idCliente,
        }),
      );
    } catch (error) {
      toast.error("Erro ao adicionar usuário.");
    }
  };

  const handleRemoveEncaminhado = async (itemString: string) => {
    try {
      const partes = itemString.split(" - ");
      const nomeReal =
        partes.length > 1 ? partes[partes.length - 1] : itemString;

      await dispatch(
        actionDeleteAlertasUsuariosEncaminhados({
          nomeUsuario: nomeReal,
          idLoja: idLojaTarget || 0,
          idCliente: user.usuario.idCliente,
        }),
      ).unwrap();

      toast.success(`${nomeReal} removido desta loja.`);

      // Atualiza a lista
      dispatch(
        actionGetAlertasUsuariosEncaminhados({
          idLoja: idLojaTarget,
          idRegional: idRegionalTarget,
          idCliente: user.usuario.idCliente,
        }),
      );
    } catch (error) {
      toast.error("Erro ao remover usuário.");
    }
  };

  const handleSaveConfig = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!lojaData) return;

    const rawDigits = storeConfig.valorLoja.replace(/\D/g, "");
    const valorFloat = Number(rawDigits) / 100;

    const payload = {
      ...lojaData,
      nome: storeConfig.nome,
      numero: storeConfig.numero,
      endereco: storeConfig.endereco,
      uf: storeConfig.uf,
      importanciaSegurada: valorFloat,
      ativo: storeConfig.ativo,
      latitude: storeConfig.latitude ? parseFloat(storeConfig.latitude) : null,
      longitude: storeConfig.longitude
        ? parseFloat(storeConfig.longitude)
        : null,
      cnpj: storeConfig.cnpj,
      vigencia: storeConfig.vigencia
        ? new Date(storeConfig.vigencia).toISOString()
        : null,
    };

    const resultAction = await dispatch(actionUpdateLojaConfig(payload));

    if (actionUpdateLojaConfig.fulfilled.match(resultAction)) {
      dispatch(actionGetLojaConfig(idLojaTarget));
      toast.success("Dados da loja atualizados com sucesso!");
    } else {
      toast.error("Erro ao atualizar dados da loja.");
    }
  };

  return (
    <BaseLayout>
      <div className="config-loja-container">
        <header className="page-header">
          <h1>
            <Store size={28} /> Configuração da Loja - {lojaData?.nome}
          </h1>
          <p>Gerencie metas financeiras e alertas de usuários</p>
        </header>

        <div className="dashboard-grid">
          <div className="card config-card">
            <div className="card-header">
              <h3>Dados Cadastrais & Financeiros</h3>
              <FileText className="icon-bg" />
            </div>
            <form onSubmit={handleSaveConfig} className="form-loja">
              <div className="form-row">
                <div className="input-group">
                  <label>Nome da Loja</label>
                  <input
                    title={""}
                    type="text"
                    name="nome"
                    value={storeConfig.nome}
                    onChange={handleChange}
                    disabled={statusUpdate === "loading"}
                  />
                </div>
                <div className="input-group shrink">
                  <label>Número</label>
                  <input
                    title={""}
                    type="text"
                    name="numero"
                    value={storeConfig.numero}
                    onChange={handleChange}
                    disabled={statusUpdate === "loading"}
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="input-group">
                  <label>
                    <MapPin size={16} /> Endereço
                  </label>
                  <input
                    title={""}
                    type="text"
                    name="endereco"
                    value={storeConfig.endereco}
                    onChange={handleChange}
                    disabled={statusUpdate === "loading"}
                  />
                </div>
                <div className="input-group shrink">
                  <label>UF</label>
                  <input
                    title={""}
                    type="text"
                    name="uf"
                    value={storeConfig.uf}
                    onChange={handleChange}
                    maxLength={2}
                    disabled={statusUpdate === "loading"}
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="input-group">
                  <label>
                    <DollarSign size={16} /> Valor Representativo (R$)
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={storeConfig.valorLoja}
                    onChange={handleChangeValor}
                    disabled={statusUpdate === "loading"}
                  />
                </div>
                <div className="input-group checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="ativo"
                      checked={storeConfig.ativo}
                      onChange={handleCheckboxChange}
                      disabled={statusUpdate === "loading"}
                    />
                    <span className="text">
                      <Activity size={16} /> Loja Ativa
                    </span>
                  </label>
                </div>
              </div>

              <div className="form-row">
                <div className="input-group">
                  <label>CNPJ</label>
                  <input
                    type="text"
                    name="cnpj"
                    value={storeConfig.cnpj}
                    onChange={handleChange}
                    placeholder="00.000.000/0000-00"
                  />
                </div>
                <div className="input-group">
                  <label>Vigência</label>
                  <input
                    title={""}
                    type="date"
                    name="vigencia"
                    // Se a vigência no estado for timestamp, precisa converter para YYYY-MM-DD aqui
                    value={
                      new Date(storeConfig.vigencia).toISOString().split("T")[0]
                    }
                    onChange={(e) =>
                      setStoreConfig((prev) => ({
                        ...prev,
                        vigencia: new Date(e.target.value).getTime(),
                      }))
                    }
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="input-group">
                  <label>
                    <Target size={16} /> Latitude
                  </label>
                  <input
                    type="text"
                    name="latitude"
                    value={storeConfig.latitude}
                    onChange={handleChange}
                    placeholder="-23.5505"
                    disabled={statusUpdate === "loading" || loadingGeo}
                  />
                </div>
                <div className="input-group">
                  <label>Longitude</label>
                  <input
                    type="text"
                    name="longitude"
                    value={storeConfig.longitude}
                    onChange={handleChange}
                    placeholder="-46.6333"
                    disabled={statusUpdate === "loading" || loadingGeo}
                  />
                </div>
                <div
                  className="input-group shrink"
                  style={{ display: "flex", alignItems: "flex-end" }}
                >
                  <button
                    type="button"
                    className="btn-geo"
                    onClick={handleGetCoordinates}
                    disabled={loadingGeo || statusUpdate === "loading"}
                  >
                    {loadingGeo ? (
                      <Loader2 className="animate-spin" size={18} />
                    ) : (
                      <Search size={18} />
                    )}
                    <span className="mobile-hide">Buscar</span>
                  </button>
                </div>
              </div>
              <div className="info-block">
                <small>
                  Divisão: {storeConfig.idDivisao} | Regional:{" "}
                  {storeConfig.idRegional} | Coordenação:{" "}
                  {storeConfig.idCoordenacao}
                </small>
              </div>
              <button
                type="submit"
                className="btn-save"
                disabled={statusUpdate === "loading"}
              >
                {statusUpdate === "loading" ? (
                  <>
                    <Loader2 className="animate-spin" size={18} /> Salvando...
                  </>
                ) : (
                  <>
                    <Save size={18} /> Salvar Alterações
                  </>
                )}
              </button>
            </form>
          </div>

          <div className="card encaminhados-card">
            <div className="card-header">
              <h3>
                <Users size={20} /> Usuários Vinculados
              </h3>
              <span className="badge">{listaEncaminhados?.length || 0}</span>
            </div>
            <div className="user-list-wrapper">
              {listaNomesUsuariosPorLoja.length === 0 ? (
                <div className="empty-state">Nenhum usuário disponível.</div>
              ) : (
                <ul className="user-list">
                  {!Array.isArray(listaNomesUsuariosPorLoja) ||
                  listaNomesUsuariosPorLoja.length === 0 ? (
                    <div className="empty-state">
                      Nenhum usuário disponível.
                    </div>
                  ) : (
                    [...listaNomesUsuariosPorLoja]
                      .filter(
                        (item) =>
                          item && typeof item.nomeFormatado === "string",
                      ) // FILTRA apenas o que for válido
                      .sort((a, b) =>
                        a.nomeFormatado.localeCompare(b.nomeFormatado),
                      )
                      .map((itemObj) => {
                        const item = itemObj.nomeFormatado;
                        const isEncaminhado = listaEncaminhados?.some(
                          (enc: EncaminhadoItem) => {
                            const userIdMatch =
                              Number(enc.idUsuario) === Number(itemObj.id);
                            const lojaIdMatch =
                              Number(enc.idLoja) === Number(idLojaTarget);

                            return userIdMatch && lojaIdMatch;
                          },
                        );

                        return (
                          <li
                            key={itemObj.id}
                            className={`user-item ${isEncaminhado ? "active" : ""}`}
                          >
                            <span className="user-name" title={item}>
                              {item}
                            </span>
                            <button
                              className={`action-btn ${isEncaminhado ? "remove" : "add"}`}
                              onClick={() =>
                                isEncaminhado
                                  ? handleRemoveEncaminhado(item)
                                  : handleAddEncaminhado(item)
                              }
                            >
                              {isEncaminhado ? (
                                <X size={18} />
                              ) : (
                                <Plus size={18} />
                              )}
                            </button>
                          </li>
                        );
                      })
                  )}
                </ul>
              )}
            </div>
          </div>
        </div>
      </div>
    </BaseLayout>
  );
};

export default ConfigLoja;
